import React, { useMemo, useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { Button, Text } from '@dotcom/crown-ui';
import classNames from 'classnames';
import { Range } from '../../../features/card-editor/components/range/range';
import { Drawer } from '../drawer/drawer';
import { DrawerPositions } from '../drawer/drawer-types';
import { SizeDrawerProps, RangeValuePosition } from './size-drawer-types';
import styles from './size-drawer.module.scss';

export const SizeDrawer = ({
  onClose,
  isOpen,
  handleSubmit,
  sizeValue,
  sizeOnChange,
  addClass = '',
  actionButtonClass,
  cancelButtonClass,
  hasActionButton = true,
  hasCancelButton = true,
  actionButtonLabel = 'Apply',
  cancelButtonLabel = 'Cancel',
  labelRangeLeft,
  labelRangeRight,
  labelRangeMiddle,
  layoutVariant,
  rangeValuePosition = RangeValuePosition.Bottom,
  showVeil,
}: SizeDrawerProps): React.ReactElement => {
  const classes = classNames(styles['size-drawer'], addClass);

  const { register, setValue } = useForm({
    defaultValues: {
      sizeRange: sizeValue,
    },
  });

  const [rangeValue, setRangeValue] = useState(sizeValue);

  const onChange = (currentRangeValue: number) => {
    setRangeValue(currentRangeValue.toString());
    if (sizeOnChange) {
      sizeOnChange(currentRangeValue.toString());
    }
  };

  useEffect(() => {
    setRangeValue(sizeValue);
    setValue('sizeRange', sizeValue);
  }, [sizeValue]);

  const HeaderContent = useMemo((): React.ReactElement => {
    return (
      <div className={classNames(styles['header-wrapper'])}>
        {rangeValuePosition && rangeValuePosition === 'Top' && (
          <div className={styles['size-range-label']}>
            <Text weight="bold" as="span" style={{ color: '#562b9b' }}>{`${rangeValue}`}</Text>
          </div>
        )}
        <Range
          domId={'size'}
          label={labelRangeMiddle}
          testId="range-testid"
          min={5}
          max={60}
          tickCount={0}
          onChange={onChange}
          register={register('sizeRange')}
          leftLabel={labelRangeLeft}
          rightLabel={labelRangeRight}
          initialValue={Number(sizeValue)}
        />
        {rangeValuePosition && rangeValuePosition === 'Bottom' && (
          <Text as="span" color="textPrimary">{`${rangeValue}`}</Text>
        )}
      </div>
    );
  }, [rangeValue]);

  const FooterContent = (): React.ReactElement => {
    return (
      <div className={styles['footer-wrapper']}>
        {hasCancelButton && (
          <Button
            className={classNames(styles['footer-buttons'], cancelButtonClass)}
            onClick={onClose}
            variant="secondary"
          >
            {cancelButtonLabel}
          </Button>
        )}
        {hasActionButton && handleSubmit && (
          <Button
            className={classNames(styles['footer-buttons'], actionButtonClass)}
            onClick={handleSubmit}
            variant="primary"
          >
            {actionButtonLabel}
          </Button>
        )}
      </div>
    );
  };

  return (
    <Drawer
      addClass={classes}
      hideCloseButton={true}
      drawerPosition={DrawerPositions.Bottom}
      onClose={onClose}
      isOpen={isOpen}
      headerContent={HeaderContent}
      showVeil={showVeil}
      role="toolbar"
      ariaLabel="Size Drawer"
      layoutVariant={layoutVariant}
    >
      <FooterContent />
      <Text as="span" styleVariant="screen-reader">
        Size Drawer
      </Text>
    </Drawer>
  );
};
